import { HTTPMethod } from "@fusionauth/typescript-client";
import {
  CameraAlt,
  QuestionAnswer,
  RefreshOutlined,
  SwitchCamera,
} from "@mui/icons-material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RestorePageIcon from "@mui/icons-material/RestorePage";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import {
  ColumnsPanelTrigger,
  DataGridPremium,
  FilterPanelTrigger,
  GRID_DETAIL_PANEL_TOGGLE_FIELD,
  GridActionsCellItem,
  type GridColDef,
  GridFilterListIcon,
  type GridRowParams,
  Toolbar,
} from "@mui/x-data-grid-premium";
import React, { useContext, useEffect, useState } from "react";
import CloseArrowTooltip from "../../../../components/CloseArrowTooltip";
import DatagridQuickFilter from "../../../../components/datagrid/DatagridQuickFilter";
import { TableToolbarButton } from "../../../../components/datagrid/TableToolbarButton";
import { ProjectContext } from "../../../../contexts/ProjectContext";
import { useFetch } from "../../../../services/hooks/useFetch";
import { apiPrefix, deviceApiPrefix } from "../../../../utils/main";
import SnapshotDetailPanel from "./SnapshotDetailPanel";

interface dicomImageLoadRequest {
  sourceFolder: string;
}

interface dialogTypeSetting {
  title: string;
  action: () => void;
}

const dialogTypes = {
  triggerSnapshot: "triggerSnapshot",
  reloadNotes: "reloadNotes",
  loadRadiologyImages: "loadRadiologyImages",
  reloadProms: "reloadProms",
} as const;

type dialogTypeKey = (typeof dialogTypes)[keyof typeof dialogTypes];

function ExpandMore() {
  return <ExpandMoreIcon type="icon" fontSize="inherit" />;
}

function ChevronRight() {
  return <ChevronRightIcon type="icon" fontSize="inherit" />;
}

export default function ProjectSnapshotsPage() {
  const theme = useTheme();
  const { id, name, isLocked, projectSettings, sourceId } =
    useContext(ProjectContext) || {};
  const radiologyImagesEnabled =
    projectSettings
      ?.filter((s) => s?.name === "INCLUDE_RADIOLOGY")?.[0]
      ?.value?.toUpperCase() === "IMAGES";
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<dialogTypeKey | null>(null);
  const {
    data: snapshotData,
    fetch: getSnapshots,
    isLoading,
  } = useFetch<SnapshotVersion[]>();
  const { create: triggerMessage } = useFetch<null>();
  const mostRecentSnapshotSucceeded =
    snapshotData?.toSorted(
      (a, b) =>
        new Date(b.createdAt).valueOf() - new Date(a.createdAt).valueOf(),
    )[0]?.status === "SUCCEEDED";

  const { data: source, fetch: getSource } = useFetch<Source>();
  const [dicomImageFolder, setDicomImageFolder] = useState("");
  const { create: postRadiologyImageTransfer } = useFetch<
    null,
    dicomImageLoadRequest
  >();
  const [dialogSnapshotId, setDialogSnapshotId] = useState<string | null>(null);
  const [snapshotType, setSnapshotType] = useState<string>("FULL");

  const getDetailPanelContent = React.useCallback(
    (params: GridRowParams<SnapshotVersion>) => (
      <SnapshotDetailPanel row={params.row} />
    ),
    [],
  );

  function CustomToolBar() {
    return (
      <Toolbar>
        <TableToolbarButton
          startIcon={<RefreshOutlined />}
          onClick={() => getSnapshots(`${apiPrefix}/projects/${id}/snapshots`)}
          disabled={isLoading}
        >
          Refresh
        </TableToolbarButton>
        <ColumnsPanelTrigger
          render={<TableToolbarButton startIcon={<ViewColumnIcon />} />}
        >
          Columns
        </ColumnsPanelTrigger>
        <FilterPanelTrigger
          render={<TableToolbarButton startIcon={<GridFilterListIcon />} />}
        >
          Filters
        </FilterPanelTrigger>
        <CloseArrowTooltip title={isLocked ? "This project is locked." : ""}>
          <span>
            <TableToolbarButton
              startIcon={<CameraAlt />}
              onClick={() => {
                setDialogOpen(true);
                setDialogType(dialogTypes.triggerSnapshot);
              }}
              disabled={isLocked}
            >
              Trigger New Snapshot
            </TableToolbarButton>
          </span>
        </CloseArrowTooltip>
        <CloseArrowTooltip
          title={
            isLocked
              ? "This project is locked."
              : !mostRecentSnapshotSucceeded
                ? "Snapshot not Successful"
                : ""
          }
        >
          <span>
            <TableToolbarButton
              startIcon={<QuestionAnswer fontSize="small" />}
              onClick={() => {
                setDialogOpen(true);
                setDialogType(dialogTypes.reloadProms);
              }}
              disabled={isLocked || !mostRecentSnapshotSucceeded}
            >
              Reload PROMs
            </TableToolbarButton>
          </span>
        </CloseArrowTooltip>
        {radiologyImagesEnabled && (
          <CloseArrowTooltip
            title={
              isLocked
                ? "This project is locked."
                : !mostRecentSnapshotSucceeded
                  ? "Latest Snapshot not Successful"
                  : ""
            }
          >
            <span>
              <TableToolbarButton
                startIcon={<SwitchCamera />}
                onClick={() => {
                  setDialogOpen(true);
                  setDialogType(dialogTypes.loadRadiologyImages);
                }}
                disabled={isLocked || !mostRecentSnapshotSucceeded}
              >
                Load DICOM Images
              </TableToolbarButton>
            </span>
          </CloseArrowTooltip>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <DatagridQuickFilter searchColumns={""} />
      </Toolbar>
    );
  }

  const snapshotDataTableFields: GridColDef<SnapshotVersion>[] = [
    {
      field: "createdAt",
      headerName: "Created",
      width: 200,
      type: "dateTime",
      valueGetter: (value) => new Date(value),
    },
    {
      field: "availableAt",
      headerName: "Available",
      width: 200,
      type: "dateTime",
      valueGetter: (value) => new Date(value),
    },
    {
      field: "number",
      headerName: "Snapshot",
      width: 100,
      type: "number",
    },
    {
      field: "type",
      headerName: "Type",
      width: 100,
      type: "singleSelect",
      valueOptions: ["FULL", "APPEND_PATIENTS", "APPEND_DATE_RANGE"],
    },
    {
      field: "id",
      headerName: "Snapshot ID",
      width: 150,
    },
    {
      field: "folder",
      headerName: "Folder",
      width: 175,
    },
    {
      field: "dataVersion",
      headerName: "DataVersion",
      width: 150,
      renderCell: (params) => {
        const version = params.row.dataVersion;
        const cutoff = params.row.datasetCutoffDate?.slice(0, 10);
        return (
          <Typography variant={"body2"}>
            {version}
            <br />
            {cutoff}
          </Typography>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 150,
      valueGetter: (value: string) =>
        value === "INPROGRESS" ? "in progress" : value.toLocaleLowerCase(),
      renderCell: (params) => {
        let color;
        let text;
        switch (params.row.status.toLocaleLowerCase()) {
          case "succeeded":
            color = "green";
            text = "Succeeded";
            break;
          case "failed":
            color = "red";
            text = "Failed";
            break;
          default:
            color = "inherit";
            text = "In Progress";
            break;
        }
        return <Typography color={color}>{text}</Typography>;
      },
      type: "singleSelect",
      valueOptions: ["succeeded", "failed", "in progress"],
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      align: "center",
      getActions: ({ row }) => {
        const snapshotSucceeded =
          row.status.toLocaleLowerCase() === "succeeded";
        return [
          <CloseArrowTooltip
            title={
              isLocked
                ? "This project is locked."
                : !snapshotSucceeded
                  ? "Snapshot not Successful"
                  : ""
            }
          >
            <span>
              <GridActionsCellItem
                key={row.id}
                icon={<RestorePageIcon fontSize="small" />}
                title="Reload Notes"
                label="Reload Notes"
                className="textPrimary"
                onClick={() => {
                  setDialogOpen(true);
                  setDialogType(dialogTypes.reloadNotes);
                  setDialogSnapshotId(row.id);
                }}
                color="inherit"
                disabled={isLocked || !snapshotSucceeded}
              />
            </span>
          </CloseArrowTooltip>,
        ];
      },
    },
  ];

  const doSnapshot = function () {
    void triggerMessage({
      path: `${apiPrefix}/projects/${id}/snapshots?type=${snapshotType}`,
      method: HTTPMethod.POST,
      postResponseAction: () => {
        // re-fetch snapshots
        void getSnapshots(`${apiPrefix}/projects/${id}/snapshots`);
        setSnapshotType("FULL");
        handleCloseDialog();
      },
    });
  };

  const doNoteLoad = function () {
    if (!dialogSnapshotId) return;
    void triggerMessage({
      path: `${apiPrefix}/projects/${id}/snapshots/${dialogSnapshotId}/noteLoad`,
      method: HTTPMethod.POST,
      postResponseAction: () => {
        // re-fetch snapshots
        void getSnapshots(`${apiPrefix}/projects/${id}/snapshots`);
        handleCloseDialog();
      },
    });
  };

  const doDicomLoad = function () {
    const requestBody = {
      sourceFolder: dicomImageFolder,
    };
    void postRadiologyImageTransfer({
      path: `${apiPrefix}/projects/${id}/snapshots/radiologyImageTransfer`,
      body: requestBody,
      method: HTTPMethod.POST,
      postResponseAction: () => {
        handleCloseDialog();
        setDicomImageFolder("");
      },
    });
  };

  const doPromLoad = function () {
    void triggerMessage({
      path: `${apiPrefix}/projects/${id}/snapshots/promLoad`,
      method: HTTPMethod.POST,
      postResponseAction: () => {
        // re-fetch snapshots
        void getSnapshots(`${apiPrefix}/projects/${id}/snapshots`);
        handleCloseDialog();
      },
    });
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    // this timeout prevents the weird flash of the dynamic modal contents
    // disappearing before the dialog actually transitions out
    setTimeout(() => {
      setDialogType(null);
    }, theme.transitions?.duration.leavingScreen);
  };

  const dialogTypeSettings: Record<dialogTypeKey, dialogTypeSetting> = {
    triggerSnapshot: {
      title: "Trigger Snapshot",
      action: doSnapshot,
    },
    reloadNotes: {
      title: "Reload Notes",
      action: doNoteLoad,
    },
    loadRadiologyImages: {
      title: "Load Radiology Images",
      action: doDicomLoad,
    },
    reloadProms: {
      title: "Reload PROMs",
      action: doPromLoad,
    },
  };

  useEffect(() => {
    void getSnapshots(`${apiPrefix}/projects/${id}/snapshots`);
  }, [getSnapshots, id]);

  useEffect(() => {
    void getSource(`${deviceApiPrefix}/sources/${sourceId}`);
  }, [getSource, sourceId]);

  return (
    <Grid container direction={"column"} className="company-settings-item">
      <div className="company-settings-item">
        <Grid>
          <Typography variant="h4">Project Snapshots</Typography>
        </Grid>
        <br />
        <Grid>
          <Typography variant="body2">
            Trigger and view snapshot information.
          </Typography>
        </Grid>
        <br />
        <Grid>
          <DataGridPremium
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
              pinnedColumns: {
                left: [GRID_DETAIL_PANEL_TOGGLE_FIELD],
              },
            }}
            loading={isLoading}
            showToolbar={true}
            slots={{
              toolbar: CustomToolBar,
              detailPanelExpandIcon: ChevronRight,
              detailPanelCollapseIcon: ExpandMore,
            }}
            slotProps={{
              loadingOverlay: {
                variant: "linear-progress",
              },
            }}
            pagination
            pageSizeOptions={[10, 20, { value: -1, label: "All" }]}
            getRowHeight={() => "auto"}
            density="compact"
            getRowId={(row) => row.id}
            columns={snapshotDataTableFields}
            rows={snapshotData || []}
            rowSelection={false}
            getDetailPanelHeight={() => 600}
            getDetailPanelContent={getDetailPanelContent}
            sx={{
              "& .MuiDataGrid-detailPanel": {
                overflow: "visible",
              },
            }}
          />
        </Grid>
      </div>
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {dialogType && dialogTypeSettings[dialogType].title}
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="alert-dialog-description"
            color={"red"}
            variant={"h6"}
          >
            Project: {name}
          </DialogContentText>
          {dialogType === dialogTypes.loadRadiologyImages && (
            <Stack direction="column" spacing={1.5}>
              <p>
                Enter the folder path containing the radiology images in the
                source bucket.
              </p>
              <TextField
                label="Source Folder"
                sx={{ width: "25ch" }}
                size="small"
                value={dicomImageFolder}
                onChange={(e) => setDicomImageFolder(e.target.value)}
                placeholder={"src/folder/"}
              />
            </Stack>
          )}
          {dialogType === dialogTypes.reloadNotes && (
            <>
              <p>
                Are you sure you want to trigger a new note load for this
                project?
              </p>
              <p>
                {radiologyImagesEnabled
                  ? " This will require transferring radiology images again once complete."
                  : ""}
              </p>
            </>
          )}
          {dialogType === dialogTypes.triggerSnapshot && (
            <Stack direction="column" spacing={1.5}>
              <p>
                Are you sure you want to trigger a new snapshot for this
                project? This could change the underlying source data currently
                being used (patients, encounters, notes, etc.).
              </p>
              <Grid>
                <Typography variant="caption">Data source cutoff:</Typography>
                <Typography variant="body1" sx={{ width: "50ch" }}>
                  {source?.datasetCutoffDate.slice(0, 10)}
                </Typography>
              </Grid>
              <FormControl>
                <FormLabel required>Select Snapshot Type</FormLabel>
                <RadioGroup row>
                  <FormControlLabel
                    checked={snapshotType === "FULL"}
                    value="FULL"
                    control={<Radio />}
                    label="Full"
                    onChange={() => setSnapshotType("FULL")}
                  />
                  <FormControlLabel
                    checked={snapshotType === "APPEND_DATE_RANGE"}
                    value="date_append"
                    control={<Radio />}
                    label="Append Date Range"
                    onChange={() => setSnapshotType("APPEND_DATE_RANGE")}
                  />
                </RadioGroup>
              </FormControl>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant={"outlined"} onClick={handleCloseDialog}>
            Cancel
          </Button>
          <Button
            variant={"contained"}
            onClick={() => {
              if (dialogType) {
                dialogTypeSettings[dialogType].action();
              }
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}
